/**
 * Minimal vector/quaternion math over *plain* objects. The sim state has to be
 * JSON-serializable and content-hashable (AD-007a), so it cannot hold
 * `three.Vector3`/`three.Quaternion` instances — but the frame convention is
 * three.js's: right-handed, Y up, +X east, −Z north, so a renderer can copy
 * these straight into `Object3D.position`/`.quaternion` with no axis swizzle.
 *
 * Body axes follow from that convention: +X right wing, +Y cockpit up,
 * forward is −Z. The body rate triple therefore maps to an angular-velocity
 * vector of `(pitch, −yaw, −roll)`: pitch-up is a rotation about +X, yaw-right
 * about −Y, roll-right about −Z.
 */

export interface Vec3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface Quat {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly w: number;
}

/** Body-axis angular rates in rad/s, signed nose-up / right-wing-down / nose-right. */
export interface BodyRates {
  readonly roll: number;
  readonly pitch: number;
  readonly yaw: number;
}

export const ZERO_VEC3: Vec3 = { x: 0, y: 0, z: 0 };
export const IDENTITY_QUAT: Quat = { x: 0, y: 0, z: 0, w: 1 };
export const ZERO_RATES: BodyRates = { roll: 0, pitch: 0, yaw: 0 };

export function vec3(x: number, y: number, z: number): Vec3 {
  return { x, y, z };
}

export function addVec3(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}

export function scaleVec3(v: Vec3, factor: number): Vec3 {
  return { x: v.x * factor, y: v.y * factor, z: v.z * factor };
}

export function dotVec3(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

export function crossVec3(a: Vec3, b: Vec3): Vec3 {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  };
}

export function lengthVec3(v: Vec3): number {
  return Math.sqrt(dotVec3(v, v));
}

/** Normalizes `v`, returning `fallback` when the vector is too short to have a direction. */
export function normalizeVec3(v: Vec3, fallback: Vec3): Vec3 {
  const length = lengthVec3(v);
  if (length < 1e-9) return fallback;
  return scaleVec3(v, 1 / length);
}

export function conjugateQuat(q: Quat): Quat {
  return { x: -q.x, y: -q.y, z: -q.z, w: q.w };
}

export function multiplyQuat(a: Quat, b: Quat): Quat {
  return {
    x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
    y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
    z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
    w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
  };
}

export function normalizeQuat(q: Quat): Quat {
  const length = Math.sqrt(q.x * q.x + q.y * q.y + q.z * q.z + q.w * q.w);
  if (length < 1e-9) return IDENTITY_QUAT;
  return { x: q.x / length, y: q.y / length, z: q.z / length, w: q.w / length };
}

export function quatFromAxisAngle(axis: Vec3, angleRad: number): Quat {
  const unit = normalizeVec3(axis, { x: 0, y: 1, z: 0 });
  const half = angleRad / 2;
  const s = Math.sin(half);
  return { x: unit.x * s, y: unit.y * s, z: unit.z * s, w: Math.cos(half) };
}

/** Rotates a vector from body frame into world frame by `q` (body → world). */
export function rotateVec3(q: Quat, v: Vec3): Vec3 {
  const t = scaleVec3(crossVec3({ x: q.x, y: q.y, z: q.z }, v), 2);
  return addVec3(addVec3(v, scaleVec3(t, q.w)), crossVec3({ x: q.x, y: q.y, z: q.z }, t));
}

/** Rotates a world-frame vector into the body frame of `q`. */
export function inverseRotateVec3(q: Quat, v: Vec3): Vec3 {
  return rotateVec3(conjugateQuat(q), v);
}

/** Converts body rates to the body-frame angular-velocity vector (see the module note). */
export function ratesToAngularVelocity(rates: BodyRates): Vec3 {
  return { x: rates.pitch, y: -rates.yaw, z: -rates.roll };
}

/**
 * Advances an attitude quaternion by a body-rate triple using the exact
 * axis-angle exponential rather than the first-order `q + ½ωq dt` update, so
 * long integrations stay on the unit sphere (determinism matters more here
 * than the handful of trig calls it costs).
 */
export function integrateAttitude(q: Quat, rates: BodyRates, dt: number): Quat {
  const omega = ratesToAngularVelocity(rates);
  const rate = lengthVec3(omega);
  if (rate < 1e-12) return q;
  return normalizeQuat(multiplyQuat(q, quatFromAxisAngle(omega, rate * dt)));
}

/**
 * AD-007n change 1: advances an attitude as **three decoupled Euler axes** —
 * `ψ += r·dt`, `θ += q·dt`, `φ += p·dt` — instead of integrating the rate
 * triple as a rigid-body angular velocity.
 *
 * The difference is not a refinement, it is the whole of AD-007n's first
 * change. `integrateAttitude` carries the rigid-body kinematics
 * `φ̇ = p + (q sin φ + r cos φ) tan θ` with it, so a *pure elevator input in a
 * bank rolls the aeroplane*: the flight-feel session measured the port rolling
 * itself from 26° to 87° of bank with the aileron released, where `RB.EXE`
 * holds the bank frozen at 37–48° through the same manoeuvre and at 68–71°
 * through a steeper one. AD-007m had already found this exact coupling in the
 * *precession* term and routed that one term around it (`applyWorldYaw`); the
 * elevator was never given the same treatment. flight-tunables.md §8.1
 * finding 7 is the positive statement of it — "aileron produced 0.00° of pitch,
 * elevator 0.00° of roll" — which the quaternion form satisfies only at zero
 * bank.
 *
 * **Over the top.** `pitchAngle` folds into [−90°, +90°] by construction, so an
 * attitude past the vertical is expressed as a smaller pitch with the bank
 * rolled through 180°. Crossing the pole is therefore a reflection rather than a
 * clamp: the pitch reflects back off ±90° and heading and bank each gain π,
 * which is the same attitude continued rather than a stop. A clamp would let a
 * held loop park the nose on the pole, where heading and bank stop being
 * separable.
 *
 * Bit-exact for a zero rate triple — the early return is what keeps trimmed
 * hands-off flight constant to the last bit rather than merely close, since a
 * decompose/recompose round trip is not the identity in floating point.
 */
export function advanceEulerAttitude(q: Quat, rates: BodyRates, dt: number): Quat {
  if (rates.roll === 0 && rates.pitch === 0 && rates.yaw === 0) return q;
  let heading = headingAngle(q) + rates.yaw * dt;
  let pitch = pitchAngle(q) + rates.pitch * dt;
  let bank = bankAngle(q) + rates.roll * dt;
  if (pitch > HALF_PI) {
    pitch = Math.PI - pitch;
    heading += Math.PI;
    bank += Math.PI;
  } else if (pitch < -HALF_PI) {
    pitch = -Math.PI - pitch;
    heading += Math.PI;
    bank += Math.PI;
  }
  return attitudeFromAngles(heading, pitch, bank);
}

const HALF_PI = Math.PI / 2;

/** The world-vertical axis in this module's heading convention (see `attitudeFromAngles`). */
const WORLD_YAW_AXIS: Vec3 = { x: 0, y: -1, z: 0 };

/**
 * Rotates `v` about the world-vertical axis by `deltaRad` — an *extrinsic*
 * (world-frame) heading change, as opposed to `integrateAttitude`'s
 * body-frame rates. This is the kinematic counterpart AD-007h's produced
 * turn-rate mechanism uses to curve the velocity vector directly, since a
 * produced rate is commanded rather than derived from a body-frame torque.
 */
export function yawAboutWorldUp(v: Vec3, deltaRad: number): Vec3 {
  if (deltaRad === 0) return v;
  return rotateVec3(quatFromAxisAngle(WORLD_YAW_AXIS, deltaRad), v);
}

/**
 * Prepends a world-frame heading rotation to `q` — the orientation
 * counterpart of `yawAboutWorldUp`, used so the produced turn carries the
 * nose around with the flight path (AD-007h) rather than opening a sideslip
 * against it. Left-multiplication composes it *after* whatever rotation `q`
 * already represents, i.e. outside the body-rate integration.
 */
export function applyWorldYaw(q: Quat, deltaRad: number): Quat {
  if (deltaRad === 0) return q;
  return normalizeQuat(multiplyQuat(quatFromAxisAngle(WORLD_YAW_AXIS, deltaRad), q));
}

/**
 * The world-horizontal axis perpendicular to `v`, pointing right of it —
 * the axis a flight path pitches about (AD-007n change 3). `fallback` is
 * returned when `v` is vertical or too short to have a direction, where no
 * horizontal perpendicular exists.
 */
export function pathLateralAxis(v: Vec3, fallback: Vec3): Vec3 {
  return normalizeVec3(crossVec3(v, { x: 0, y: 1, z: 0 }), fallback);
}

/**
 * Raises (positive) or lowers the flight path by `deltaRad` about a
 * world-horizontal `axis` — AD-007n's produced path curvature, the vertical
 * plane's counterpart to AD-007h's `yawAboutWorldUp`. Speed and compass
 * heading are both preserved: the rotation is about the axis perpendicular to
 * both the path and the vertical.
 */
export function pitchAboutWorldAxis(v: Vec3, axis: Vec3, deltaRad: number): Vec3 {
  if (deltaRad === 0) return v;
  return rotateVec3(quatFromAxisAngle(axis, deltaRad), v);
}

/**
 * The orientation counterpart of `pitchAboutWorldAxis` — the same world-frame
 * rotation, prepended, so the aeroplane goes round with its flight path as one
 * rigid body. That is exactly what leaves the angle of attack and the sideslip
 * where they were, which is AD-007n's point: the attitude *follows* the path
 * rather than being commanded independently of it.
 */
export function applyWorldPitch(q: Quat, axis: Vec3, deltaRad: number): Quat {
  if (deltaRad === 0) return q;
  return normalizeQuat(multiplyQuat(quatFromAxisAngle(axis, deltaRad), q));
}

export const BODY_FORWARD: Vec3 = { x: 0, y: 0, z: -1 };
export const BODY_UP: Vec3 = { x: 0, y: 1, z: 0 };
export const BODY_RIGHT: Vec3 = { x: 1, y: 0, z: 0 };

/**
 * Rotates `v` about the aircraft's *own* yaw axis (body up, as `q` points it)
 * by `deltaRad`, right-positive — the same sense as `yawAboutWorldUp`, and
 * identical to it whenever the wings are level, since that is exactly when the
 * two axes coincide.
 *
 * AD-007j's path alignment uses this rather than the world vertical because
 * sideslip is a *body-frame* angle: β is the velocity's component along the
 * span axis, so the rotation that closes it is the one about the axis
 * perpendicular to both — the aircraft's yaw axis. A world-vertical rotation
 * agrees at zero bank (where the measurement was taken) and diverges from it
 * as the aircraft banks, to the point of not closing β at all at 90°, where
 * the span axis is vertical and a horizontal rotation cannot touch it.
 */
export function yawAboutBodyUp(v: Vec3, q: Quat, deltaRad: number): Vec3 {
  if (deltaRad === 0) return v;
  const axis = rotateVec3(q, BODY_UP);
  return rotateVec3(quatFromAxisAngle({ x: -axis.x, y: -axis.y, z: -axis.z }, deltaRad), v);
}

/**
 * Bank angle in radians, positive right-wing-down, derived from where the body
 * "up" axis points relative to the world vertical.
 */
export function bankAngle(q: Quat): number {
  const up = rotateVec3(q, BODY_UP);
  const right = rotateVec3(q, BODY_RIGHT);
  return Math.atan2(-right.y, up.y);
}

/** Pitch (flight-path-independent nose elevation) in radians, positive nose-up. */
export function pitchAngle(q: Quat): number {
  const forward = rotateVec3(q, BODY_FORWARD);
  return Math.asin(Math.max(-1, Math.min(1, forward.y)));
}

/** Compass-style heading in radians, 0 = −Z (north), increasing clockwise from above. */
export function headingAngle(q: Quat): number {
  const forward = rotateVec3(q, BODY_FORWARD);
  return Math.atan2(forward.x, -forward.z);
}

/** Builds an attitude from heading/pitch/bank, applied in that order. */
export function attitudeFromAngles(headingRad: number, pitchRad: number, bankRad: number): Quat {
  const yaw = quatFromAxisAngle({ x: 0, y: -1, z: 0 }, headingRad);
  const pitch = quatFromAxisAngle(BODY_RIGHT, pitchRad);
  const bank = quatFromAxisAngle({ x: 0, y: 0, z: -1 }, bankRad);
  return normalizeQuat(multiplyQuat(multiplyQuat(yaw, pitch), bank));
}

/**
 * Linear interpolation between two positions. Render-support math (AD-007a:
 * "render interpolates between the last two sim states" is part of the
 * engine's contract, not app-side glue) — paired with `slerpQuat` below for
 * interpolating a full pose in `interpolate.ts`.
 */
export function lerpVec3(from: Vec3, to: Vec3, alpha: number): Vec3 {
  return {
    x: from.x + (to.x - from.x) * alpha,
    y: from.y + (to.y - from.y) * alpha,
    z: from.z + (to.z - from.z) * alpha,
  };
}

/** Below this angle between the two attitudes, nlerp and slerp are indistinguishable. */
const LINEAR_SLERP_THRESHOLD = 0.9995;

/**
 * Shortest-arc spherical interpolation. A quaternion and its negation are the
 * same rotation, so the sign flip below is what keeps a tick that crosses
 * ±180° from spinning the aircraft the long way round for one frame.
 */
export function slerpQuat(from: Quat, to: Quat, alpha: number): Quat {
  let dot = from.x * to.x + from.y * to.y + from.z * to.z + from.w * to.w;
  const target = dot < 0 ? { x: -to.x, y: -to.y, z: -to.z, w: -to.w } : to;
  dot = Math.abs(dot);

  if (dot > LINEAR_SLERP_THRESHOLD) {
    return normalizeQuat({
      x: from.x + (target.x - from.x) * alpha,
      y: from.y + (target.y - from.y) * alpha,
      z: from.z + (target.z - from.z) * alpha,
      w: from.w + (target.w - from.w) * alpha,
    });
  }

  const theta = Math.acos(Math.min(dot, 1));
  const sinTheta = Math.sin(theta);
  const fromWeight = Math.sin((1 - alpha) * theta) / sinTheta;
  const toWeight = Math.sin(alpha * theta) / sinTheta;
  return normalizeQuat({
    x: from.x * fromWeight + target.x * toWeight,
    y: from.y * fromWeight + target.y * toWeight,
    z: from.z * fromWeight + target.z * toWeight,
    w: from.w * fromWeight + target.w * toWeight,
  });
}
